// Salesforce セキュリティ監査モックのデータ層。カタログ正本は @cloud-checker/shared に移動済み。
// モックの結果/履歴/スナップショット/AI を提供する。実 SF/Bedrock 連携は後工程。
import {
  SF_AUDIT_CHECKS,
  SF_AUDIT_CATEGORIES,
  type Check,
  type CheckCategory,
  type Severity,
  type CategoryDef,
} from "@cloud-checker/shared";
import type { Tone } from "../ui/tokens";
import { SEVERITY_ORDER as UI_SEVERITY_ORDER } from "../ui/severity-badge";
import { escapeCsvField } from "../ui/csv";

// 既存の消費者が ./audit-data から型/定数をインポートし続けられるよう再エクスポート
export type { Check, CheckCategory, Severity, CategoryDef };
export const CHECKS: readonly Check[] = SF_AUDIT_CHECKS;
export const CATEGORIES: readonly CategoryDef[] = SF_AUDIT_CATEGORIES;

export const CATEGORY_KEYS: readonly CheckCategory[] = [
  "identity-access",
  "permissions-sharing",
  "external-exposure",
  "account-hygiene-audit",
];

// 並び順は共有 ui/severity-badge を正本にする(GW と同一)。文言だけドメイン固有。
export const SEVERITY_ORDER: Record<Severity, number> = UI_SEVERITY_ORDER;

export const SEVERITY_LABELS: Record<Severity, string> = {
  critical: "重大",
  high: "高",
  medium: "中",
  low: "低",
};

export type FindingStatus = "pass" | "fail" | "na" | "review";

export interface Finding {
  readonly check: Check;
  readonly status: FindingStatus;
  readonly affectedCount: number;
  readonly affectedResources: readonly string[];
  readonly lastDetected: string;
}

export interface SeverityCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export const SEVERITY_WEIGHT: Record<Severity, number> = {
  critical: 10,
  high: 5,
  medium: 2,
  low: 1,
};

export function computeSeverityCounts(findings: readonly Finding[]): SeverityCounts {
  const counts: SeverityCounts = { critical: 0, high: 0, medium: 0, low: 0 };
  for (const f of findings) {
    if (f.status === "fail") counts[f.check.severity] += 1;
  }
  return counts;
}

export function computeScore(findings: readonly Finding[]): number {
  let applicable = 0;
  let passed = 0;
  for (const f of findings) {
    if (f.status === "na" || f.status === "review") continue;
    const w = SEVERITY_WEIGHT[f.check.severity];
    applicable += w;
    if (f.status === "pass") passed += w;
  }
  if (applicable === 0) return 100;
  return Math.round((100 * passed) / applicable);
}

export interface CategoryStat {
  score: number;
  pass: number;
  fail: number;
  na: number;
  review: number;
  total: number;
}

export function categoryStats(findings: readonly Finding[], category: CheckCategory): CategoryStat {
  const inCat = findings.filter((f) => f.check.category === category);
  const pass = inCat.filter((f) => f.status === "pass").length;
  const fail = inCat.filter((f) => f.status === "fail").length;
  const na = inCat.filter((f) => f.status === "na").length;
  const review = inCat.filter((f) => f.status === "review").length;
  return { score: computeScore(inCat), pass, fail, na, review, total: inCat.length };
}

// --- モックの直近実行結果 -------------------------------------------------
// 要対応(FAIL)/対象外(NA)を固定 id で割当て、残りは PASS。スコアは computeScore で導出。
const FAIL_IDS: ReadonlySet<string> = new Set([
  "mfa-enforced-all-logins",
  "modify-all-data-minimized",
  "guest-user-owd-private",
  "connected-app-oauth-scopes-least-privilege",
  "session-timeout-and-force-logout",
  "login-ip-ranges-on-profiles",
  "owd-sensitive-objects-private",
  "clickjack-protection-enabled",
  "password-min-length-complexity",
  "inactive-users-deactivated",
  "setup-audit-trail-and-login-history",
  "cors-allowlist-minimized",
]);
const NA_IDS: ReadonlySet<string> = new Set([
  "sso-saml-and-mydomain-policy",
  "event-monitoring-enabled",
  "transaction-security-threat-detection",
]);

const SAMPLE_RESOURCES: Record<CheckCategory, readonly string[]> = {
  "identity-access": ["System Administrator プロファイル", "Integration User", "Standard User プロファイル"],
  "permissions-sharing": ["Account (OWD)", "Custom: Invoice__c", "Sales 権限セット"],
  "external-exposure": ["Customer Community Guest User", "公開サイト: support", "Connected App: DataLoader"],
  "account-hygiene-audit": ["休眠ユーザー 7名", "Setup Audit Trail", "CSV エクスポート権限保有 4名"],
};

function buildLatestFindings(): readonly Finding[] {
  return CHECKS.map((check, i) => {
    const status: FindingStatus = NA_IDS.has(check.id) ? "na" : FAIL_IDS.has(check.id) ? "fail" : "pass";
    const affectedCount = status === "fail" ? ((i % 5) + 1) : 0;
    const resources = SAMPLE_RESOURCES[check.category];
    return {
      check,
      status,
      affectedCount,
      affectedResources: status === "fail" ? resources.slice(0, Math.min(affectedCount, resources.length)) : [],
      lastDetected: "2026-06-09T01:30:00Z",
    };
  });
}

export const LATEST_FINDINGS: readonly Finding[] = buildLatestFindings();
export const LATEST_SCORE: number = computeScore(LATEST_FINDINGS);
export const LATEST_COUNTS: SeverityCounts = computeSeverityCounts(LATEST_FINDINGS);

export function scoreBand(score: number): string {
  if (score >= 90) return "Excellent";
  if (score >= 80) return "Very Good";
  if (score >= 70) return "Good";
  if (score >= 55) return "Poor";
  return "Very Poor";
}

// --- 設定スナップショット + 差分計算 ---------------------------------------
export type SnapshotKind = "auto" | "manual";

export interface ConfigSetting {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly category: CheckCategory;
}

export interface Snapshot {
  readonly id: string;
  readonly takenAt: string;
  readonly kind: SnapshotKind;
  readonly takenBy: string;
  readonly settings: readonly ConfigSetting[];
}

export type ChangeType = "added" | "removed" | "modified";

export interface ConfigChange {
  readonly key: string;
  readonly label: string;
  readonly before: string | null;
  readonly after: string | null;
  readonly changeType: ChangeType;
  readonly severityImpact: Severity;
}

const SETTING_SEVERITY: Record<string, Severity> = {
  "Session.Timeout": "high",
  "Password.MinLength": "high",
  "Network.LoginIpRanges": "high",
  "Security.Clickjack": "high",
  "Guest.OWD.Account": "critical",
  "ConnectedApp.DataLoader.Scopes": "critical",
};

function settingSeverity(key: string): Severity {
  return SETTING_SEVERITY[key] ?? "medium";
}

export function computeConfigDiff(
  prev: readonly ConfigSetting[],
  curr: readonly ConfigSetting[],
): readonly ConfigChange[] {
  const prevMap = new Map(prev.map((p) => [p.key, p]));
  const currMap = new Map(curr.map((c) => [c.key, c]));
  const changes: ConfigChange[] = [];
  for (const c of curr) {
    const p = prevMap.get(c.key);
    if (p === undefined) {
      changes.push({ key: c.key, label: c.label, before: null, after: c.value, changeType: "added", severityImpact: settingSeverity(c.key) });
    } else if (p.value !== c.value) {
      changes.push({ key: c.key, label: c.label, before: p.value, after: c.value, changeType: "modified", severityImpact: settingSeverity(c.key) });
    }
  }
  for (const p of prev) {
    if (!currMap.has(p.key)) {
      changes.push({ key: p.key, label: p.label, before: p.value, after: null, changeType: "removed", severityImpact: settingSeverity(p.key) });
    }
  }
  return changes;
}

const SETTINGS_T0: readonly ConfigSetting[] = [
  { key: "Session.Timeout", label: "セッションタイムアウト", value: "8時間", category: "identity-access" },
  { key: "Password.MinLength", label: "パスワード最小長", value: "8", category: "account-hygiene-audit" },
  { key: "Network.LoginIpRanges", label: "Login IP Range(管理者)", value: "未設定", category: "identity-access" },
  { key: "Security.Clickjack", label: "クリックジャック保護", value: "有効", category: "external-exposure" },
  { key: "Guest.OWD.Account", label: "ゲスト OWD: Account", value: "Public Read", category: "external-exposure" },
];

const SETTINGS_T1: readonly ConfigSetting[] = [
  { key: "Session.Timeout", label: "セッションタイムアウト", value: "2時間", category: "identity-access" },
  { key: "Password.MinLength", label: "パスワード最小長", value: "8", category: "account-hygiene-audit" },
  { key: "Security.Clickjack", label: "クリックジャック保護", value: "有効", category: "external-exposure" },
  { key: "Guest.OWD.Account", label: "ゲスト OWD: Account", value: "Private", category: "external-exposure" },
  { key: "ConnectedApp.DataLoader.Scopes", label: "Connected App スコープ(DataLoader)", value: "full, refresh_token", category: "identity-access" },
];

export const SNAPSHOTS: readonly Snapshot[] = [
  { id: "snap-2026-06-08", takenAt: "2026-06-08T00:10:00Z", kind: "auto", takenBy: "system", settings: SETTINGS_T0 },
  { id: "snap-2026-06-09", takenAt: "2026-06-09T00:10:00Z", kind: "auto", takenBy: "system", settings: SETTINGS_T1 },
];

export const LATEST_CHANGE_COUNT: number = computeConfigDiff(
  SNAPSHOTS[0]!.settings,
  SNAPSHOTS[1]!.settings,
).length;

// --- 実行履歴 / スケジュール / 接続 / AI モック + CSV 生成 ------------------
export interface AuditRun {
  readonly id: string;
  readonly ranAt: string;
  readonly score: number;
  readonly counts: SeverityCounts;
  readonly runBy: string;
  readonly status: "completed" | "running" | "failed";
}

export const RUN_HISTORY: readonly AuditRun[] = [
  { id: "run-3", ranAt: "2026-06-09T01:30:00Z", score: LATEST_SCORE, counts: LATEST_COUNTS, runBy: "test-admin@soramie.jp", status: "completed" },
  { id: "run-2", ranAt: "2026-06-02T01:30:00Z", score: 61, counts: { critical: 5, high: 8, medium: 3, low: 1 }, runBy: "system", status: "completed" },
  { id: "run-1", ranAt: "2026-05-26T01:30:00Z", score: 58, counts: { critical: 5, high: 9, medium: 4, low: 1 }, runBy: "system", status: "completed" },
];

export type Frequency = "off" | "weekly" | "monthly";
export interface Schedule {
  readonly frequency: Frequency;
  readonly dayOfWeek: number; // 0=日
  readonly dayOfMonth: number; // 1-28
  readonly time: string; // "HH:mm"
  readonly notifyTo: readonly string[];
}
// --- ジョブ別スケジュール（設定チェック / 設定スナップショット / ユーザー棚卸し） ---
export type JobKind = "config-check" | "snapshot" | "user-reconcile";

export interface JobDef {
  readonly kind: JobKind;
  readonly label: string;
  readonly tone: Tone; // カレンダー色分け/凡例バッジで使用
}

export const JOB_DEFS: readonly JobDef[] = [
  { kind: "config-check", label: "設定チェック", tone: "info" },
  { kind: "snapshot", label: "設定スナップショット", tone: "success" },
  { kind: "user-reconcile", label: "ユーザー棚卸し", tone: "warning" },
];

export const DEFAULT_JOB_SCHEDULES: Record<JobKind, Schedule> = {
  "config-check": { frequency: "weekly", dayOfWeek: 1, dayOfMonth: 1, time: "09:00", notifyTo: ["test-admin@soramie.jp"] },
  snapshot: { frequency: "monthly", dayOfWeek: 1, dayOfMonth: 1, time: "00:10", notifyTo: ["test-admin@soramie.jp"] },
  "user-reconcile": { frequency: "monthly", dayOfWeek: 1, dayOfMonth: 1, time: "10:00", notifyTo: ["test-admin@soramie.jp"] },
};

export interface Connection {
  readonly connected: boolean;
  readonly orgName: string;
  readonly orgId: string;
  readonly connectedAt: string;
  readonly environment: "production" | "sandbox";
}
export const CONNECTION: Connection = {
  connected: true,
  orgName: "Shinonome Laboratory (本番)",
  orgId: "00D5j000000XXXXEAA",
  connectedAt: "2026-06-01T04:00:00Z",
  environment: "production",
};

export interface AiTopFinding {
  readonly checkId: string;
  readonly reason: string;
  readonly action: string;
}
export interface AiAnalysis {
  readonly summary: string;
  readonly topFindings: readonly AiTopFinding[];
  readonly generatedAt: string;
}
export const AI_ANALYSIS: AiAnalysis = {
  summary:
    "Health Check スコアは " + LATEST_SCORE + "% で、Salesforce Baseline Standard を下回っています。最優先はログイン経路の保護（MFA 未強制）と外部公開（ゲスト OWD の公開）です。これらは認証情報の窃取や未認証アクセスによるデータ漏洩に直結します。次点で管理データ権限（Modify All Data）と接続アプリの過剰スコープを是正してください。",
  topFindings: [
    { checkId: "mfa-enforced-all-logins", reason: "全ログインで MFA が未強制。フィッシング・認証情報窃取の最大経路。", action: "Identity Verification と権限セットで全ユーザーに MFA を必須化する。" },
    { checkId: "guest-user-owd-private", reason: "ゲストの外部 OWD が公開。未認証ユーザーにレコードが露出。", action: "対象オブジェクトの外部 OWD を Private にし Secure Guest Access を有効化する。" },
    { checkId: "connected-app-oauth-scopes-least-privilege", reason: "Connected App に full スコープ。トークン漏洩時の影響が甚大。", action: "OAuth スコープを業務最小（api / refresh_token 等）に限定する。" },
  ],
  generatedAt: "2026-06-09T01:35:00Z",
};

export const AI_SUGGESTED_PROMPTS: readonly string[] = [
  "重大(critical)の検出だけ優先度順に説明して",
  "MFA を全社展開する手順を教えて",
  "ゲストユーザーの露出を最小化する設定変更案を出して",
  "前回スナップショットからの設定変更の影響を評価して",
];

export interface AiMessage {
  readonly role: "user" | "assistant";
  readonly text: string;
  readonly ts: string;
}

export function findingsToCsv(findings: readonly Finding[]): string {
  const header = ["id", "category", "severity", "title", "status", "affectedCount", "remediation"];
  const rows = findings.map((f) =>
    [f.check.id, f.check.category, f.check.severity, f.check.title, f.status, String(f.affectedCount), f.check.remediation]
      .map(escapeCsvField)
      .join(","),
  );
  return [header.join(","), ...rows].join("\n") + "\n";
}

// --- ユーザー棚卸し（モック: 実 SF ユーザー API は後工程） ---
export interface SfUser {
  readonly username: string;
  readonly email: string;
  readonly name: string;
  readonly profile: string;
  readonly active: boolean;
  readonly lastLogin: string;
}

export const SF_USERS: readonly SfUser[] = [
  { username: "sato@example.co.jp.prod", email: "sato@example.co.jp", name: "佐藤 一郎", profile: "System Administrator", active: true, lastLogin: "2026-06-08 18:02" },
  { username: "suzuki@example.co.jp.prod", email: "suzuki@example.co.jp", name: "鈴木 花子", profile: "Standard User", active: true, lastLogin: "2026-06-09 09:15" },
  { username: "takahashi@example.co.jp.prod", email: "takahashi@example.co.jp", name: "高橋 健太", profile: "Standard User", active: true, lastLogin: "2026-06-09 08:40" },
  { username: "tanaka@example.co.jp.prod", email: "tanaka@example.co.jp", name: "田中 美咲", profile: "Sales User", active: true, lastLogin: "2026-06-05 17:21" },
  { username: "watanabe@example.co.jp.prod", email: "watanabe@example.co.jp", name: "渡辺 大輔", profile: "Sales User", active: true, lastLogin: "2026-06-09 10:03" },
  { username: "ito@example.co.jp.prod", email: "ito@example.co.jp", name: "伊藤 さくら", profile: "Service User", active: true, lastLogin: "2026-06-08 13:55" },
  { username: "yamamoto@example.co.jp.prod", email: "yamamoto@example.co.jp", name: "山本 拓也", profile: "Standard User", active: true, lastLogin: "2026-03-31 18:45" },
  { username: "nakamura@example.co.jp.prod", email: "nakamura@example.co.jp", name: "中村 由佳", profile: "Sales User", active: true, lastLogin: "2026-04-30 19:12" },
  { username: "kobayashi@example.co.jp.prod", email: "kobayashi@example.co.jp", name: "小林 直人", profile: "Standard User", active: false, lastLogin: "2025-12-26 17:00" },
  { username: "kato@example.co.jp.prod", email: "kato@example.co.jp", name: "加藤 恵", profile: "Service User", active: false, lastLogin: "2026-01-30 16:30" },
];

// SmartHR 風ヘッダのサンプル従業員CSV。アクティブ8名中6名を含む
// （yamamoto/nakamura が含まれない=退職者アカウント残存の疑い2件として検出される構成）。
// shinjin は SF 未作成(参考表示)の例。
export const SAMPLE_ROSTER_CSV: string = [
  "社員番号,氏名,業務メールアドレス,在籍状況",
  "1001,佐藤 一郎,sato@example.co.jp,在籍",
  "1002,鈴木 花子,suzuki@example.co.jp,在籍",
  "1003,高橋 健太,takahashi@example.co.jp,在籍",
  "1004,田中 美咲,tanaka@example.co.jp,在籍",
  "1005,渡辺 大輔,watanabe@example.co.jp,在籍",
  "1006,伊藤 さくら,ito@example.co.jp,在籍",
  "1007,新人 太郎,shinjin@example.co.jp,在籍",
].join("\n") + "\n";
